import React from 'react';
import { motion } from 'motion/react';
import { Search, Scissors, Calendar, CheckCircle, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';

export const BookingFlow = () => {
  const steps = [
    {
      icon: Search,
      title: 'Cari Kapster',
      description: 'Filter berdasarkan lokasi, rating, dan spesialisasi untuk menemukan partner gaya yang tepat.',
      color: 'from-blue-500 to-indigo-600',
      lightColor: 'bg-blue-50/80',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-600',
      label: 'Temukan'
    },
    {
      icon: Scissors,
      title: 'Pilih Layanan',
      description: 'Dari Classic Cut hingga Full Grooming – pilih paket yang sesuai dengan kebutuhan Anda.',
      color: 'from-emerald-400 to-teal-600',
      lightColor: 'bg-emerald-50/80',
      borderColor: 'border-emerald-200',
      textColor: 'text-emerald-600',
      label: 'Kustomisasi'
    },
    {
      icon: Calendar,
      title: 'Atur Jadwal',
      description: 'Sistem booking real-time yang memastikan Anda tidak perlu lagi mengantri di barbershop.',
      color: 'from-amber-400 to-orange-500',
      lightColor: 'bg-amber-50/80',
      borderColor: 'border-amber-200',
      textColor: 'text-amber-600',
      label: 'Presisi'
    },
    {
      icon: CheckCircle,
      title: 'Konfirmasi',
      description: 'Dapatkan notifikasi instan dan pengingat jadwal langsung ke perangkat Anda.',
      color: 'from-slate-700 to-slate-900',
      lightColor: 'bg-slate-100/80',
      borderColor: 'border-slate-300',
      textColor: 'text-slate-800',
      label: 'Instan'
    }
  ];

  return (
    <section className="py-32 bg-white relative overflow-hidden" id="how-it-works">
      {/* Background Decorative Elements */}
      <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-emerald-100/20 blur-[120px] rounded-full -translate-y-1/2 translate-x-1/4 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-blue-100/20 blur-[120px] rounded-full translate-y-1/2 -translate-x-1/4 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 relative">
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-1.5 bg-slate-100 text-slate-600 rounded-full text-[10px] font-black uppercase tracking-[0.2em] mb-6 shadow-sm"
          >
            <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
            Panduan Reservasi
          </motion.div>
          
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-6xl font-black text-slate-900 tracking-tighter mb-6 lg:leading-[1.1]"
          >
            Proses Cerdas, <br className="hidden md:block" />
            Penampilan <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-600 italic">Berkelas.</span>
          </motion.h2>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-slate-500 font-medium max-w-2xl mx-auto leading-relaxed"
          >
            Teknologi booking termutakhir yang dirancang untuk semua gender — baik pria maupun wanita. 
            Hemat waktu, kustomisasi gaya terbaik Anda, tanpa hambatan.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 + 0.3, duration: 0.8, ease: "circOut" }}
              className="relative group h-full"
            >
              <div className={cn(
                "h-full p-8 rounded-[3rem] border-2 transition-all duration-500 relative overflow-hidden",
                step.lightColor,
                step.borderColor,
                "shadow-[0_20px_40px_-20px_rgba(0,0,0,0.05)]",
                "hover:shadow-[0_32px_64px_-16px_rgba(0,0,0,0.1)] group-hover:-translate-y-2"
              )}>
                {/* Visual Accent */}
                <div className={cn(
                    "absolute top-0 right-0 w-24 h-24 bg-gradient-to-br opacity-5 rounded-bl-[4rem]",
                    step.color
                )} />

                <div className="flex justify-between items-start mb-10">
                  <div className={cn(
                    "w-16 h-16 rounded-2xl flex items-center justify-center bg-gradient-to-br shadow-xl group-hover:scale-110 transition-transform duration-500 ring-4 ring-white/50",
                    step.color
                  )}>
                    <step.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-sm font-black text-slate-300 tracking-widest">
                      0{index + 1}
                    </span>
                    <div className={cn("w-6 h-1 rounded-full mt-1 bg-gradient-to-r", step.color)} />
                  </div>
                </div>

                <div className="space-y-4">
                  <div className={cn(
                    "inline-block px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-[0.2em] bg-white shadow-sm border border-slate-100",
                    step.textColor
                  )}>
                    {step.label}
                  </div>
                  <h3 className={cn(
                    "text-xl font-black tracking-tight leading-tight uppercase",
                    step.textColor
                  )}>
                    {step.title}
                  </h3>
                  <p className="text-sm text-slate-600 font-medium leading-relaxed">
                    {step.description}
                  </p>
                </div>

                {/* Decorative Connector (Desktop) */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-[20%] -right-4 translate-x-1/2 z-20 pointer-events-none">
                    <motion.div 
                        animate={{ x: [0, 5, 0] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="w-10 h-10 bg-white border-2 border-slate-50 rounded-2xl flex items-center justify-center shadow-lg transform rotate-45"
                    >
                      <ChevronRight className="w-5 h-5 text-slate-400 -rotate-45" />
                    </motion.div>
                  </div>
                )}
              </div>
              
              {/* Outer Glow Effect */}
              <div className={cn(
                "absolute -inset-2 opacity-0 group-hover:opacity-20 blur-3xl transition-opacity duration-700 -z-10 bg-gradient-to-br",
                step.color
              )} />
            </motion.div>
          ))}
        </div>


        {/* Bottom Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.9 }}
          className="mt-24 p-12 bg-slate-900 rounded-[3rem] text-center relative overflow-hidden shadow-2xl"
        >
          {/* Decorative background for CTA box */}
          <div className="absolute top-0 left-0 w-full h-full opacity-20 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/3 h-full bg-gradient-to-r from-emerald-500/30 to-transparent blur-3xl" />
            <div className="absolute bottom-0 right-0 w-1/3 h-full bg-gradient-to-l from-blue-500/30 to-transparent blur-3xl" />
          </div>

          <div className="relative z-10">
            <h3 className="text-2xl md:text-3xl font-black text-white mb-6 uppercase tracking-tight">
              Siap untuk <span className="text-emerald-400 italic underline decoration-wavy underline-offset-8">Berubah?</span>
            </h3>
            <button 
              onClick={() => document.getElementById('kapster-unggulan')?.scrollIntoView({ behavior: 'smooth' })}
              className="group px-12 py-5 bg-white text-slate-900 rounded-2xl text-[12px] font-black uppercase tracking-widest hover:bg-emerald-500 hover:text-white transition-all shadow-xl active:scale-95 inline-flex items-center gap-3"
            >
              Mulai Petualangan Gaya Anda
              <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <p className="mt-6 text-slate-400 text-xs font-bold uppercase tracking-widest opacity-60">
              Tanpa biaya admin • Konfirmasi Instan • 24/7
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
